"use client";

import "@/app/(public)/shared/tokens/animations.css";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Button } from "@/app/(public)/shared/ui/Button";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Building, Crown, Gift, Group, Home, HotAirBalloon, Megaphone, Spark, User } from "iconoir-react";
import { useRouter } from "next/navigation";
import React, { useRef } from "react";
import Image from "next/image";
import startupsLandscapeGraphic from "@/app/(public)/shared/assets/graphics/startups-landscape.png";
import enterpriseLandscapeGraphic from "@/app/(public)/shared/assets/graphics/enterprise-landscape.png";

interface UserTypeCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  delay?: number;
  inView?: boolean;
  href: string;
}

const UserTypeRow = ({ title, description, icon, href }: UserTypeCardProps) => {
  const router = useRouter();

  const handleClick = () => {
    window.open(href, "_blank");
  };

  return (
    <div
      className="w-full flex items-center justify-between gap-4 md:gap-6 transition-all duration-200 group bg-transparent hover:bg-landing-secondary/30 rounded-xl md:rounded-2xl cursor-pointer h-full md:p-0"
      onClick={handleClick}
    >
      <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
        <div className="w-16 md:w-20 h-16 md:h-20 rounded-lg flex items-center justify-center bg-landing-secondary shrink-0 group-hover:bg-landing-secondary/80 transition-colors duration-200 [&>*]:group-hover:!text-indigo-500 [&>*]:transition-colors [&>*]:duration-200">
          {icon}
        </div>
        <div className="flex flex-col flex-1 min-w-0 gap-1">
          <h3 className="text-tag-md text-landing-primary">{title}</h3>
          <p className="text-body-sm text-landing-secondary">{description}</p>
        </div>
      </div>
      <ArrowRight
        className="w-4 md:w-5 h-4 md:h-5 text-gray-400 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all duration-200"
        strokeWidth={1.75}
      />
    </div>
  );
};

export const WhoWeBuildFor = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const router = useRouter();

  const companySizeTypes = [
    {
      title: "For startups",
      description: "Plan retreats without a planner—built for lean teams.",
      icon: <Spark className="w-7 h-7 text-landing-primary" strokeWidth={1.75} />,
      href: "/solutions/startups",
    },
    {
      title: "For enterprises",
      description: "Centralized tracking and preferred rates for events.",
      icon: <Building className="w-7 h-7 text-landing-primary" strokeWidth={1.75} />,
      href: "/solutions/enterprise",
    },
  ];

  const roleBasedTypes = [
    {
      title: "For people teams",
      description: "Offsites, kickoffs, and all-hands made simple.",
      icon: <Group className="w-7 h-7 text-landing-primary" strokeWidth={1.75} />,
      href: "/people-teams",
    },
    {
      title: "For executive assistants",
      description: "Automate venue sourcing and logistics.",
      icon: <Crown className="w-7 h-7 text-landing-primary" strokeWidth={1.75} />,
      href: "/executive-assistants",
    },
    {
      title: "For event marketers",
      description: "Host field events with less back-and-forth.",
      icon: <Megaphone className="w-7 h-7 text-landing-primary" strokeWidth={1.75} />,
      href: "/event-marketers",
    },
    {
      title: "For agencies",
      description: "Source smarter with less manual work.",
      icon: <HotAirBalloon className="w-7 h-7 text-landing-primary" strokeWidth={1.75} />,
      href: "/agencies",
    },
    {
      title: "For travel agents",
      description: "Coordinate group bookings with ease.",
      icon: <User className="w-7 h-7 text-landing-primary" strokeWidth={1.75} />,
      href: "/travel-agents",
    },
    {
      title: "For hoteliers & venues",
      description: "Connect with event planners seamlessly.",
      icon: <Home className="w-7 h-7 text-landing-primary" strokeWidth={1.75} />,
      href: "/venues",
    },
  ];

  return (
    <div
      className="w-full flex justify-center items-center py-12 md:py-24 relative"
      data-section="landing-who-we-built-for"
    >
      <div className="w-full max-w-none md:p-16 flex flex-col gap-8 md:outline md:outline-4 md:outline-offset-[-4px] md:outline-[var(--landing-border-light)] md:rounded-3xl">
        {/* Header Section */}
        <motion.div
          className="flex flex-col w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col justify-start items-start gap-4">
            <h2 className="text-h2 max-w-[530px] text-landing-primary">Who we're built for</h2>
            <p className="max-w-[530px] text-body-lg text-landing-secondary">
              Whether you're planning your first offsite or managing dozens of events a year, Nowadays adapts to your
              needs.
            </p>
            <Button variant="secondary" color="primary" size="sm" onClick={() => window.open("/referral", "_blank")}>
              Refer a friend and earn $250
            </Button>
          </div>
        </motion.div>

        <div className="w-full flex flex-col gap-4 md:gap-8">
          {/* Company Size Section - Feature1 Style */}
          <div ref={ref} className="w-full flex md:flex-row flex-col gap-4 md:gap-6">
            {/* Left Feature */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex flex-col gap-4 cursor-pointer group flex-1"
              onClick={() => window.open(companySizeTypes[0].href, "_blank")}
            >
              <div className="h-48 md:h-[320px] bg-landing-secondary rounded-xl md:rounded-2xl group-hover:bg-landing-secondary/80 transition-colors duration-200 overflow-hidden">
                <Image
                  src={startupsLandscapeGraphic}
                  alt="Startups solution illustration"
                  className="w-full h-full object-cover"
                  priority
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col gap-1 flex-1">
                  <h3 className="text-tag-lg text-landing-primary">{companySizeTypes[0].title}</h3>
                  <p className="text-body text-landing-secondary">{companySizeTypes[0].description}</p>
                </div>
                <div>
                  <Button variant="secondary" color="primary" size="sm">
                    Learn more
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Right Feature */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col gap-4 cursor-pointer group flex-1"
              onClick={() => window.open(companySizeTypes[1].href, "_blank")}
            >
              <div className="h-48 md:h-[320px] bg-landing-secondary rounded-xl md:rounded-2xl group-hover:bg-landing-secondary/80 transition-colors duration-200 overflow-hidden">
                <Image
                  src={enterpriseLandscapeGraphic}
                  alt="Enterprise solution illustration"
                  className="w-full h-full object-cover"
                  priority
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col gap-1 flex-1">
                  <h3 className="text-tag-lg font-medium text-landing-primary">{companySizeTypes[1].title}</h3>
                  <p className="text-body text-landing-secondary">{companySizeTypes[1].description}</p>
                </div>
                <div>
                  <Button variant="secondary" color="primary" size="sm">
                    Learn more
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>

          {/* divider */}
          <div className="w-full h-[1px] bg-gray-200" />

          {/* Role-Based Section - Grid Layout */}
          <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 auto-rows-fr">
            {roleBasedTypes.map((userType, index) => (
              <div key={index} className="w-full">
                <UserTypeRow {...userType} delay={0.3 + index * 0.1} inView={inView} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
